import { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slider,
} from '@mui/material';
import {
  Delete,
  MoreVert,
  FlipToFront,
  FlipToBack,
  Settings,
} from '@mui/icons-material';
import type { ImageElement as ImageElementType } from '@/types/thinkspace';

type ImageElementProps = {
  element: ImageElementType;
  selected: boolean;
  canEdit: boolean;
  onSelect: () => void;
  onUpdate?: (updates: Partial<ImageElementType>) => void;
  onDelete: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
  onDragStart?: (e: React.MouseEvent | React.TouchEvent) => void;
};

export const ImageElement = ({
  element,
  selected,
  canEdit,
  onSelect,
  onUpdate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onDragStart,
}: ImageElementProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [propsOpen, setPropsOpen] = useState(false);
  const [opacity, setOpacity] = useState(element.opacity);
  const [borderRadius, setBorderRadius] = useState(element.borderRadius);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  // Sync state with element when it changes
  useEffect(() => {
    setOpacity(element.opacity);
    setBorderRadius(element.borderRadius);
  }, [element.opacity, element.borderRadius]);

  // Handle resize
  const handleResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    setIsResizing(true);
    setResizeStart({
      x: clientX,
      y: clientY,
      width: element.size.width,
      height: element.size.height,
    });
  };

  useEffect(() => {
    if (!isResizing || !resizeStart) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;

      const dx = clientX - resizeStart.x;

      // Maintain aspect ratio
      const aspectRatio = resizeStart.width / resizeStart.height;
      let newWidth = Math.max(50, resizeStart.width + dx);
      let newHeight = newWidth / aspectRatio;

      if (newHeight < 50) {
        newHeight = 50;
        newWidth = newHeight * aspectRatio;
      }

      onUpdate?.({
        size: { width: newWidth, height: newHeight },
      });
    };

    const handleUp = () => {
      setIsResizing(false);
      setResizeStart(null);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove);
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isResizing, resizeStart, onUpdate]);

  return (
    <>
      <Box
        ref={elementRef}
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (canEdit) {
            setPropsOpen(true);
          }
        }}
        onMouseDown={canEdit && !propsOpen ? onDragStart : undefined}
        onTouchStart={canEdit && !propsOpen ? onDragStart : undefined}
        sx={{
          position: 'absolute',
          left: element.position.x,
          top: element.position.y,
          width: element.size.width,
          height: element.size.height,
          cursor: canEdit ? 'move' : 'default',
          zIndex: element.zIndex,
          border: selected ? '2px solid #0f2ccf' : '2px solid transparent',
          boxSizing: 'border-box',
          borderRadius: `${element.borderRadius}px`,
          overflow: 'hidden',
          userSelect: 'none',
        }}
      >
        <img
          src={element.imageUrl}
          alt=""
          draggable={false}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: element.opacity,
            borderRadius: `${element.borderRadius}px`,
            pointerEvents: 'none',
          }}
        />

        {/* Menu button */}
        {canEdit && selected && !propsOpen && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              backgroundColor: 'rgba(255,255,255,0.9)',
              boxShadow: 1,
              '&:hover': { backgroundColor: 'white' },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        )}

        {/* Resize handle */}
        {canEdit && selected && (
          <Box
            onMouseDown={handleResizeStart}
            onTouchStart={handleResizeStart}
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              backgroundColor: '#0f2ccf',
              borderRadius: '2px 0 0 0',
              '&:hover': { backgroundColor: '#1a3de0' },
            }}
          />
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); setPropsOpen(true); }}>
          <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onBringToFront(); }}>
          <ListItemIcon><FlipToFront fontSize="small" /></ListItemIcon>
          <ListItemText>Bring to Front</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onSendToBack(); }}>
          <ListItemIcon><FlipToBack fontSize="small" /></ListItemIcon>
          <ListItemText>Send to Back</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Properties Dialog */}
      <Dialog
        open={propsOpen}
        onClose={() => setPropsOpen(false)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DialogTitle>Image Properties</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, minWidth: 300 }}>
            <Typography gutterBottom>Opacity: {Math.round(opacity * 100)}%</Typography>
            <Slider
              value={opacity}
              onChange={(_, value) => setOpacity(value as number)}
              min={0.1}
              max={1}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              sx={{ mb: 3 }}
            />

            <Typography gutterBottom>Border Radius: {borderRadius}px</Typography>
            <Slider
              value={borderRadius}
              onChange={(_, value) => setBorderRadius(value as number)}
              min={0}
              max={50}
              step={1}
              valueLabelDisplay="auto"
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Size: {Math.round(element.size.width)} x {Math.round(element.size.height)} px
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPropsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              onUpdate?.({ opacity, borderRadius });
              setPropsOpen(false);
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
